import { DOCUMENT, ViewportScroller } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { Observable, throwError, of } from 'rxjs';
import {
    map,
    switchMap,
    tap,
    filter,
    take,
    concatMap,
    mergeMap,
    catchError,
} from 'rxjs/operators';

import { LoadFirstPageGrid, LoadNewCollectionCategoryProductsSuccess } from '../modules/category-grid/store/category-grid.actions';
import { moveBannersToEndUseCase } from '../modules/category-grid/use-cases/move-banners-to-end.use-case';
import { CategoryPageService } from '../services/category-page.service';

import {
    InitCategoryPage,
    LoadCategoryPage,
    LoadCategoryPageSuccess,
    LoadMenuCategoriesSuccess,
    LoadCategoryPageError,
    SetCategoryPageStyles,
    LoadCategorySuccess,
    DestroyStateCategoryPage,
    SetLastStateViewed,
    LoadProductsStudio,
    LoadSeoInfoSuccess,
    LoadNewCollectionPageSuccess,
    SetOrderProduct,
    SetFilterProduct,
    SetProductsId,
    SetFilterItemProduct,
    SetFilterIsApplied,
    InitCategoryPageSuccess,
    LoadLandingPage,
    SetCategoryProductsWithLastState,
    ResetCategoryPage,
} from './category-page.actions';
import { getCategory, getDefaultOrders, getFilters, getFiltersFilters, getLastCategoryState, getProductsId } from './category-page.selectors';
import { CategoryState } from './category-page.state';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { SeoInfo } from '@core/domain/models/seo.models';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { HideLoading, SetCurrentCategoryId, ShowLoading } from '@core/infrastructure/state/core-commands.actions';
import { getCmsConfigValue, getStore } from '@core/infrastructure/state/core.selectors';
import { CategoriesSelectors } from '@features/category';
import { CategoryPublicPort } from '@features/category/infrastructure/public/public.port';
import { getLastRequestedAudience } from '@features/category/infrastructure/state/category.selectors';
import { ProductsArrayFeaturePublicApi, ProductsArrayPublicModels } from '@features/products/productsArray';
import { getProductById } from '@features/products/productsArray/infrastructure/state/productsArray.selectors';
import { SeoActionTypes } from '@features/seo';
import { SidebarPublicPort } from '@features/sidebar';
import { UserEventAudienceService } from '@features/user-events';
import { routerPublicQueries } from '@frontend/libs/duttirouter';
import { orderTypes } from '@pages/category';
import { ProductFilterService } from '@pages/category/services/product-filter.service';
import { getImagePathFromProduct } from '@pages/product/use-cases/product-page.use-case';
import { MenuCategory } from '@shared/header/models/category.model';
import { LoadMarketLanguages, LanguagePath, isMarketModuleInitialized } from '@shared/market/store';
import { MenuCategoriesService } from '@shared/menu-categories/services/menu-categories.service';
import { ChangeCurrentCategoryAction } from '@shared/menu-categories/store/menu-categories.actions';
import { GoToHome, GoToSection, MicrositeType } from '@shared/navigation';
import {
    RelatedProductsRepositoryInterface,
} from '@shared/product/related-products/domain/related-products-repository.interface';
import { RELATED_PRODUCTS_REPOSITORY } from '@shared/product/related-products/ui/tokens/related-products.tokens';
import { ProductLoaderService } from '@shared/product/services/product-loader-service.impl';
import { isRecommendationFetchNeeded, loadRecommendationsAndSort$ } from '@shared/recommendations-by-placement/application/helpers/recommendations.helper';
import { HeraclesService } from '@shared/services/heracles/heracles.service';
import { ConfirmationModalComponent } from '@ui/dialog/components/confirmation/confirmation-modal.component';
import { DialogMargins, DialogOptions, DialogService, DialogSizes } from '@ui/dialog/dialog.service';

@Injectable()
export class CategoryPageEffects {
    private readonly catalogStylesFromCms$: Observable<any[]> = this.store.select(getCmsConfigValue('background_color'));

    private readonly getLastCategoryState$: Observable<CategoryState> = this.store.select(getLastCategoryState);

    private readonly getCategoriesNavigation$:
    Observable<MenuCategory[]> = this.store.select(CategoriesSelectors.getCategoriesNavigation);

    private readonly getDefaultOrders$: Observable<
    Partial<Record<orderTypes, number[]>>> = this.store.select(getDefaultOrders);

    private readonly getProductsId$: Observable<number[]> = this.store.select(getProductsId);

    private readonly enabledHeracles$: Observable<boolean> = this.store.select(getCmsConfigValue('enable_heracles'));

    private readonly getMenuCategories$: Observable<
    MenuCategory[]
    > = this.store.select(CategoriesSelectors.getMenuCategories);

    private readonly getStore$ = this.store.select(getStore);

    private readonly getCategory$ = this.store.select(getCategory);

    private readonly getMergedRoute$ = this.store.select(routerPublicQueries.getMergedRoute);

    private readonly isMarketModuleInitialized$ = this.store.select(isMarketModuleInitialized).pipe(
        filter((isInitialized) => isInitialized),
        take(1)
    );

    constructor(
        private readonly actions$: Actions,
        private readonly productLoaderService: ProductLoaderService,
        private readonly categoryPublicPort: CategoryPublicPort,
        private readonly store: Store,
        private readonly heraclesService: HeraclesService,
        private readonly categoryPageService: CategoryPageService,
        private readonly productsArrayPublicApi: ProductsArrayFeaturePublicApi,
        private readonly productFilterService: ProductFilterService,
        private readonly dialogService: DialogService,
        private readonly viewportScroller: ViewportScroller,
        private readonly platformService: CorePlatformService,
        private readonly menuCategoriesService: MenuCategoriesService,
        private readonly audienceService: UserEventAudienceService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        @Inject(DOCUMENT) private readonly document: Document,
        @Inject(RELATED_PRODUCTS_REPOSITORY) private readonly relatedProductsRepository: RelatedProductsRepositoryInterface,
        private readonly platform: CorePlatformService,
    ) { }

    public initModule$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCategoryPage),
            tap(() => ShowLoading()),
            switchMap(() => this.getCategoriesNavigation$.pipe(
                filter((categoriesNavigation) => categoriesNavigation?.length > 0),
                take(1),
            )),
            concatLatestFrom(() => [
                this.getMergedRoute$.pipe(filter(data => !!data)),
            ]),
            map(([categoriesNavigation, mergedRoute]) => {
                const categoryId = this.categoryPageService.parseCategoryId(mergedRoute, categoriesNavigation);
                if (!categoryId) return LoadCategoryPageError();
                return InitCategoryPageSuccess({ categoryId });
            }),
        );
    });

    public initCategoryPageSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCategoryPageSuccess),
            filter(({ categoryId }) =>
                !isRecommendationFetchNeeded(this.store)(categoryId)),
            concatLatestFrom(() => this.getLastCategoryState$),
            map(([{ categoryId }, lastState]) =>
                SetCategoryProductsWithLastState({
                    categoryId,
                    lastState: { ...lastState, isSortedPage: false },
                })),
        );
    });

    public initWithRecommendationsSort$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(InitCategoryPageSuccess),
            filter(({ categoryId }) => this.platform.isBrowser()
                && isRecommendationFetchNeeded(this.store)(categoryId)),
            concatLatestFrom(() => this.getLastCategoryState$),
            switchMap(([
                { categoryId },
                lastState,
            ]) => {
                try {
                    return loadRecommendationsAndSort$(
                        this.store,
                        this.document,
                        this.relatedProductsRepository,
                    )(categoryId);
                } catch {
                    return of({ categoryId, lastState, error: true });
                }
            }),
            map(({ categoryId, lastState, error }) => SetCategoryProductsWithLastState({
                categoryId,
                lastState: { ...lastState, isSortedPage: !error },
            })),
        );
    });

    public setCategoryProductsWithLastState$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetCategoryProductsWithLastState),
            map(({ categoryId, lastState }) => {
                if (lastState?.category?.id === categoryId) {
                    const categoryProducts: ProductsArrayPublicModels.PublicCategoryPlatformStudio = {
                        filters: lastState.filters,
                        gridElements: lastState.gridElements,
                        productIds: lastState.defaultOrders[orderTypes.RECOMMENDED],
                        sortedProductIds: lastState.defaultOrders[orderTypes.NEWEST],
                        sortedProductIdsByPricesAsc: lastState?.defaultOrders[orderTypes.PRICE_ASC],
                        sortedProductIdsByPricesDesc: lastState?.defaultOrders[orderTypes.PRICE_DESC],
                        elementDefaultImageTypes: lastState.elementDefaultImageTypes,
                    };
                    this.viewportScroller.scrollToPosition([0, 0]);
                    return LoadCategoryPageSuccess({ categoryProducts, lastState });
                }
                return LoadCategoryPage({ categoryId });
            }),
        );
    });

    // Actualmente gestionamos la Pagina de error en Categorias que es lo mismo que este fragmento de codigo ya que la navegación se hace por SPA
    // Esta validación debería contemplar que el HTML generado por Servidor no es el Mismo que por Cliente a través de las Audiencias
    // en ese caso si sería posible realizar o una navegación a Home por HTTPS para que cargue el catalogo de categorias que te tocan por Audiencia
    // public GoToHomeOnCategoryPageError$ = createEffect(() => {
    //     return this.actions$.pipe(
    //         ofType(LoadCategoryPageError),
    // eslint-disable-next-line no-secrets/no-secrets
    //         filter(({ audience }) => audience !== PublicAudienceModels.AUDIENCE_CATALOG_ID.SALES),
    //         map(() => {
    //             return GoToHome({});
    //         })
    //     );
    // });

    public openModalOnCategoryPageError$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategoryPageError),
            concatLatestFrom(() => this.store.select(getLastRequestedAudience)),
            filter(([_, isSales]) =>
                this.platformService.isBrowser() && isSales === this.audienceService.getIsSalesCookieId()),
            concatMap(() => {
                return this.dialogService.openModalFromComponent(
                    'vip.modal.title',
                    ConfirmationModalComponent,
                    {
                        description: 'vip.modal.subtitle',
                        confirm: 'common.shop_now',
                        showCancel: false,
                    },
                    DialogSizes.SIZE_S,
                    DialogMargins.MARGIN_40_40,
                    [
                        DialogOptions.CLOSE_ON_BACKDROP_CLICK,
                    ],
                ).afterClosed$.pipe(take(1));
            }),
            map(() => GoToHome({ reload: true }))
        );
    });

    public getCategoryProductsStudioPlatform$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadProductsStudio),
            switchMap(({ categoryId, attachments }) => {
                return this.productsArrayPublicApi.getCategoryPlatformStudio$(categoryId).pipe(
                    map((categoryGrid) => {
                        if (attachments?.find((atth) => atth.name === 'FORCE_BANNER_BOTTOM')) {
                            const bannersOrderConfig: Array<{ IdCategory: string[]; IdBanner: string }> = this.store.selectSignal(getCmsConfigValue('FORCE_BANNER_BOTTOM'))();
                            return moveBannersToEndUseCase(categoryGrid, categoryId, bannersOrderConfig);
                        }

                        return categoryGrid;
                    }),
                    tap((categoryGrid) => {
                        if (this.store.selectSignal(getCmsConfigValue('GET_CATEGORY_WITH_PRODUCTS'))()) {
                            this.productLoaderService.addApiProductsToList(categoryGrid.products);
                        }

                        // eslint-disable-next-line radix
                        this.productLoaderService.initPagination(categoryGrid.productIds, Number.parseInt(categoryId, 10));
                    }),
                );
            }),

            map((categoryProducts: ProductsArrayPublicModels.PublicCategoryPlatformStudio) => {
                return LoadCategoryPageSuccess({ categoryProducts });
            })
        );
    });

    public getCategorySeoInfo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadFirstPageGrid),
            filter(() => this.platform.isBrowser()),
            concatLatestFrom(() => this.store.select(getCategory)),
            switchMap(([_, cat]) =>
                this.categoryPublicPort.seoFromCategory$(cat).pipe(
                    map((seoInfo: SeoInfo) => ({ seoInfo, category: cat }))
                )),
            map(({ seoInfo, category }) =>
                LoadSeoInfoSuccess({ seoInfo, category }))
        );
    });

    public getCategorySeoInfoSsr$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadFirstPageGrid),
            filter(() => !this.platform.isBrowser()),
            concatLatestFrom(() => this.store.select(getCategory)),
            switchMap(([action, cat]): Observable<{ seoInfo: SeoInfo; category: any }> => {
                const firstProductId = action.grid.pageProductIds[0];
                return this.store.select(getProductById(firstProductId)).pipe(
                    filter(prod => !!prod),
                    switchMap(product => {
                        const imgPath = getImagePathFromProduct(product); // Obtenemos el path de la imagen
                        return this.categoryPublicPort.seoFromCategory$(cat, imgPath).pipe(
                            map((seoInfo: SeoInfo) => ({ seoInfo, category: cat }))
                        );
                    })
                );
            }),
            map(({ seoInfo, category }) =>
                LoadSeoInfoSuccess({ seoInfo, category }))
        );
    });

    public loadCategorySeo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadSeoInfoSuccess),
            map(({ seoInfo, category }) => SeoActionTypes.SetCategorySeo({ categorySeo: seoInfo, category: category })),
        );
    });

    public loadCategoryNoindex$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            map(({ category }) => SeoActionTypes.SetCategoryNoindex({ category: category })),
        );
    });

    public closePanelFilter$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            filter(() => this.platform.isBrowser()),
            tap(() => {
                SidebarPublicPort(this.store).closeSidebar();
            }),
        );
    }, { dispatch: false });

    public saveCategoryIdInContext$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            filter(() => this.platformService.isBrowser()),
            map(({ category }) => SetCurrentCategoryId({ categoryId: category?.id })),
        );
    });

    public resetCategoryIdInContext$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ResetCategoryPage, DestroyStateCategoryPage),
            map(() => SetCurrentCategoryId({ categoryId: undefined })),
        );
    });

    public loadLandingPageByCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategoryPage),
            concatLatestFrom(() => this.getCategoriesNavigation$),
            map(([{ categoryId }, categoriesNavigation]) =>
                categoriesNavigation.find((cat) => cat.id === categoryId)),
            filter((category) => category?.type === '26'),
            mergeMap((category) =>
                this.store.select(getCmsConfigValue(category.shortDescription)).pipe(
                    filter((cmsConfigValue) => !!cmsConfigValue),
                    map(() => LoadLandingPage({ landing: category }))
                ))
        );
    });

    public loadCategoryProductsByCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategoryPage),
            concatLatestFrom(() => [this.enabledHeracles$, this.getCategoriesNavigation$]),
            map(([{ categoryId }, enableHeracles, categoriesNavigation]) => {
                const category: MenuCategory = categoriesNavigation.find((cat) => cat.id === categoryId);
                return { category, categoryId, enableHeracles };
            }),
            map(({ category, categoryId, enableHeracles }) => {
                const heraclesCategoryId = enableHeracles ? this.heraclesService.getHeraclesCategoryId(category) : null;
                return {
                    categoryId: heraclesCategoryId ?? categoryId,
                    attachments: category.attachments,
                };
            }),
            map(({ categoryId, attachments }) => {
                return LoadProductsStudio({ categoryId, attachments });
            })
        );
    });

    public loadCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategoryPage),
            switchMap(({ categoryId }) => this.categoryPublicPort.getCategory$(categoryId).pipe(
                catchError((error) => {
                    return throwError(() => error);
                })
            )),
            // Las páginas que entran por url pantalla ej: es/smart-app-n1771 a categoria y no son del Dominio son redirigidas
            filter((category) => !!category),
            map((category) => {
                if (MicrositeType.includes(category.type)) {
                    if (!this.window.inditex.micrositeLegacy) {
                        return GoToSection({ url: category.locationPath, reload: true });
                    }
                }

                return LoadCategorySuccess({ category });
            }),
        );
    });

    public setMenuCategoriesOnLoadNewCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            concatLatestFrom(() => this.getMenuCategories$),
            filter(([{ category }, categories]) => !!category.id && categories?.length > 0),
            map(([{ category }, categories]) => CategoryPageService.parseMenuCategories(category, categories)),
            map(({ navMenuCategories, filterTypeUsedAsSubcategories }) => LoadMenuCategoriesSuccess({
                navMenuCategories,
                filterTypeUsedAsSubcategories,
            }))
        );
    });

    public loadCategoryTranslatedMarketLanguages$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategoryPageSuccess),
            concatMap(() => this.isMarketModuleInitialized$),
            concatLatestFrom(() => [
                this.getStore$,
                this.getCategory$,
                this.getMergedRoute$,
            ]),
            filter(([_, store, category]) =>
                category.categoryUrlTranslations?.length > 0 && store.supportedLanguages?.length > 0),
            map(([_, store, category, mergedRoute]) => {
                const languagesPath = category.categoryUrlTranslations?.map((translation) => {
                    const language = store.supportedLanguages.find((lang) => lang.id === translation.id.toString());
                    const categoryPath = translation.name;
                    const categoryUrl: string = mergedRoute.queryParams.g ? `${categoryPath}?g=${mergedRoute.queryParams.g}` : categoryPath;
                    return {
                        language,
                        url: categoryUrl,
                    } as LanguagePath;
                });
                return LoadMarketLanguages({ languagesPath });
            }),
        );
    });

    public LoadCategoryPageEnd$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategoryPageError, LoadCategoryPageSuccess),
            map(() => HideLoading())
        );
    });

    public SetCategoryPageStyles$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess),
            concatLatestFrom(() => this.catalogStylesFromCms$),
            filter(([{ category }, catalogStylesConfig]) => {
                if (category?.id && catalogStylesConfig && catalogStylesConfig.length > 0) {
                    // eslint-disable-next-line radix
                    return catalogStylesConfig.find((config: any) => config.ID.includes(Number.parseInt(category.id, 10)));
                }
                return false;
            }),
            map(([{ category }, catalogStylesConfig]) =>
                // eslint-disable-next-line radix
                catalogStylesConfig.find((config: any) => config.ID.includes(Number.parseInt(category.id, 10)))),
            map((catalogStylesConfig: any) => SetCategoryPageStyles({ catalogStyles: catalogStylesConfig.STYLE }))
        );
    });

    public onDestroySetLastStateViewed$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(DestroyStateCategoryPage),
            map(() => SetLastStateViewed()),
        );
    });

    public onLoadNewCollectionCategoryProductsSuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadNewCollectionCategoryProductsSuccess),
            map(({ data }) => {
                const elementDefaultImageTypes = data[0].categoryGrids.elementDefaultImageTypes;
                return LoadNewCollectionPageSuccess({ elementDefaultImageTypes });
            }),
        );
    });

    public changeOrderProduct = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetOrderProduct),
            concatLatestFrom(() => [
                this.getDefaultOrders$,
                this.getProductsId$,
            ]),
            map(([{ order = orderTypes.RECOMMENDED }, defaultOrders, actualProductsId]) => {
                const productsId = defaultOrders[order].filter(productId => actualProductsId.includes(productId));

                return SetProductsId({ productsId });
            })
        );
    });

    public changeFilterProduct = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetFilterProduct),
            concatLatestFrom(() => [
                this.getDefaultOrders$.pipe(filter(defaultOrders => !!Object.keys(defaultOrders).length)),
            ]),
            map(([{ filters }, defaultOrders]) => {
                const productsId = this.productFilterService.getProductIdsFiltered(filters, defaultOrders);
                return SetProductsId({ productsId });
            })
        );
    });

    public changeFilterItemProduct = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetFilterItemProduct),
            concatLatestFrom(() => [
                this.store.select(getFilters),
                this.getDefaultOrders$.pipe(filter(defaultOrders => !!Object.keys(defaultOrders).length)),
            ]),
            map(([_, filters, defaultOrders]) => {
                const productsId = this.productFilterService.getProductIdsFiltered(filters, defaultOrders);
                return SetProductsId({ productsId });
            })
        );
    });

    public setFilterIsApplied = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetFilterItemProduct),
            concatLatestFrom(() => [
                this.store.select(getFiltersFilters),
            ]),
            map(([_, filters]) => {
                const isApplied = Object.keys(filters).some(item => {
                    return filters[item].some((elem) => elem?.isChecked);
                });
                return SetFilterIsApplied({ isApplied });
            })
        );
    });

    public changeCurrentCategorySuccess$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(LoadCategorySuccess, LoadCategoryPageSuccess),
            concatLatestFrom(() => [
                this.store.select(CategoriesSelectors.getMenuCategories),
                this.menuCategoriesService.getCurrentCategoryListeningUrl$,
            ]),
            filter(([_, menuCategories, { category }]) => {
                return menuCategories?.length > 0 && !!category;
            }),
            map(([_, __, { category, categories }]) => ChangeCurrentCategoryAction({ category, categories }))
        );
    });
}
